'use client';

import { useEffect, useRef } from 'react';
import { GridToolbarQuickFilter } from '@mui/x-data-grid-premium';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faMagnifyingGlass } from '@fortawesome/pro-solid-svg-icons';
import {
  GridAddToListButton,
  GridColumnMenu,
  GridExportMenu,
  GridStatusFilterMenu,
  GridProviderDeleteButton,
  GridTrendingButton,
  GridTableDrawerButton,
} from './components';
import { useTable } from 'src/context/table';

interface TableControlsProps {
  isExporting?: boolean;
  onExport?: (type: string) => void;
  columns: any[];
  columnVisibilityModel: any;
  setColumnVisibilityModel: any;
  showExportButton?: boolean;
  showFilterButton?: boolean;
  showDeleteButton?: boolean;
  showAddToListButton?: boolean;
  showTrendingButton?: boolean;
  searchPlaceholder?: string;
  table?: string;
  isExportButtonDisabled?: boolean;
}

const TableControls = ({
  isExporting = false,
  onExport,
  columns,
  columnVisibilityModel,
  setColumnVisibilityModel,
  showExportButton = true,
  showFilterButton = false,
  showDeleteButton = false,
  showAddToListButton = false,
  showTrendingButton = false,
  searchPlaceholder = 'by Name, Specialty or Location',
  table = '',
  isExportButtonDisabled = false,
}: TableControlsProps) => {
  const ref = useRef(null);
  const { setShowIcon } = useTable();
  const MIN_WIDTH = 600;

  // We want to show icons differently per table, basing this currently off width
  useEffect(() => {
    if (!ref?.current) return;

    const handleResize = () => {
      const showIcon = ref.current.offsetWidth < MIN_WIDTH;
      setShowIcon(showIcon);
    };

    window.addEventListener('resize', handleResize);
    handleResize();
    return () => window.removeEventListener('resize', handleResize);
  }, [ref]);

  return (
    <div ref={ref} className="w-full flex flex-shrink-0">
      <div className="flex items-center">
        <div className="flex-[2] flex items-center gap-2">
          <GridTableDrawerButton location={table} />
          <div className="flex-1 flex-grow-[4] max-w-[400px]">
            <GridToolbarQuickFilter
              id={`${table ?? ''}-search-list-quick-filter`}
              variant="outlined"
              fullWidth
              sx={{
                width: '100%',
              }}
              InputProps={{
                startAdornment: (
                  <FontAwesomeIcon
                    icon={faMagnifyingGlass}
                    className="text-secondary bg-white"
                  />
                ),
                endAdornment: null,
                placeholder: searchPlaceholder,
                fullWidth: true,
                sx: {
                  borderRadius: '0.375rem',
                  height: '2.25rem',
                  width: '100%',
                  maxWidth: '400px',
                  border: 0,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  fieldset: {
                    border: '1px solid #cdd7e1',
                    /* --joy-shadow-xs */
                    boxShadow: '0px 1px 2px 0px rgba(21, 21, 21, 0.08)',
                    width: '100%',
                    maxWidth: '400px',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  },
                  input: {
                    fontSize: '0.875rem',
                    lineHeight: '1.25rem',
                    fontStyle: 'normal',
                    fontWeight: 400,
                    letterSpacing: '0.01071em',
                    color: '#32383e',
                    width: '100%',
                    maxWidth: '400px',
                    backgroundColor: 'white',
                  },
                  '&:hover': {
                    border: 0,
                    fieldset: {
                      border: '1px solid #cdd7e1',
                    },
                  },
                },
              }}
            />
          </div>
          <div className="flex-1 flex gap-1">
            {showFilterButton && <GridStatusFilterMenu table={table} />}
            {showDeleteButton && <GridProviderDeleteButton table={table} />}
            {showAddToListButton && <GridAddToListButton table={table} />}
          </div>
        </div>
        <div className="flex-1">
          <div className="flex items-center justify-end whitespace-nowrap gap-2 ml-2">
            {showExportButton && (
              <GridExportMenu
                onExport={onExport}
                isExporting={isExporting}
                table={table}
                disabled={isExportButtonDisabled}
              />
            )}
            <GridColumnMenu
              table={table}
              columns={columns}
              columnVisibilityModel={columnVisibilityModel}
              setColumnVisibilityModel={setColumnVisibilityModel}
            />
            {showTrendingButton && <GridTrendingButton table={table} />}
          </div>
        </div>
      </div>
    </div>
  );
};

export default TableControls;
